import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { CloudUpload, FileDownload } from '@mui/icons-material';
import { formatUnixTimestamp } from '../../library';

const ExportModal = ({
  open,
  onClose,
  onExport,
  tabName,
  currentStartDate,
  currentEndDate,
  loading = false,
}) => {
  const [selectedFormat, setSelectedFormat] = useState('xlsx');

  const formatOptions = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'pdf', label: 'PDF (.pdf)' },
  ];

  const handleExport = () => {
    if (!currentStartDate || !currentEndDate) {
      return;
    }
    
    const exportData = {
      format: selectedFormat,
      startDate: currentStartDate,
      endDate: currentEndDate,
      tabName,
    };
    onExport(exportData);
  };

  const handleClose = () => {
    // Reset form when closing
    setSelectedFormat('xlsx');
    onClose();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: 'primary.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloudUpload color="primary" />
          </Box>
          <Box>
            <Typography variant="h6" component="div" fontWeight="600">
              Export {tabName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose format for your export
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* Format Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="500">
              Export Format
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.300',
                  },
                }}
              >
                {formatOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FileDownload fontSize="small" />
                      <Typography>{option.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>


          {/* Preview Section */}
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom fontWeight="500">
              Export Preview
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                      Tab Name
                    </Typography>
                    <Chip 
                      label={tabName} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ width: 'fit-content' }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                      Export Format
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {formatOptions.find(opt => opt.value === selectedFormat)?.label}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                      Selected Period
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {formatUnixTimestamp(currentStartDate)} to {formatUnixTimestamp(currentEndDate)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                      File Name
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="500"
                      sx={{ 
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: 'text.secondary'
                      }}
                    >
                      ledgerX_Export_{tabName?.replace(/\s+/g, '_')}_{currentStartDate && Number.isFinite(currentStartDate) ? new Date(currentStartDate * 1000).toISOString().split('T')[0] : 'unknown_date'}.{selectedFormat}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={<CloudUpload />}
          disabled={loading || !currentStartDate || !currentEndDate}
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 500,
            minWidth: '120px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          {loading ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal;
